<template>
  <div>
    <vt-loading-button
      v-if="$_canCreateComplementary()"
      v-tooltip="showCompactView ? $t('diagnosis.btns.newComp') : ''"
      class="btn--primary btn-sm btn-block fixed-btn"
      qa="complementaries-cards-add-button"
      :params="{ promise: addComplementary }"
    >
      <i
        class="fal fa-plus"
        aria-hidden="true"
      />
      <span
        v-if="!showCompactView"
        class="ml-1"
      >{{ $tc('diagnosis.btns.complementary',1) }}</span>
    </vt-loading-button>
    <div
      v-if="complementaries.length === 0 && !showCompactView"
      class="blank-message text-center mt-3"
    >
      <b-card
        class="blank-card"
        style="opacity: .1"
      />
      <b-card
        class="blank-card mt-2"
        style="opacity: .05"
      />
      <b-card
        class="blank-card mt-2"
        style="opacity: .03"
      />
      <b-card
        class="blank-card mt-2"
        style="opacity: .01"
      />
    </div>
    <div
      v-if="complementaries.length === 0 && showCompactView"
      class="mt-3"
    >
      <b-card
        class="blank-card-compact"
        style="opacity: .1"
      />
      <b-card
        class="blank-card-compact mt-2"
        style="opacity: .05"
      />
      <b-card
        class="blank-card-compact mt-2"
        style="opacity: .03"
      />
      <b-card
        class="blank-card-compact mt-2"
        style="opacity: .01"
      />
    </div>
    <div
      v-for="comp in complementaries"
      :key="comp.code"
      class="mt-3"
    >
      <complementary-compact-card
        v-if="showCompactView"
        :complementary="comp.code"
        :is-active="cardActive === comp.comId || comp.comId === complementaryId"
        @cardSelected="cardSelected(comp)"
      />
      <b-card
        v-if="!showCompactView"
        class="complementariesCards component-inside-1--hover full-bordered mt-2"
        :class="{'card-active': cardActive === comp.comId || comp.comId === complementaryId}"
        @click="cardSelected(comp)"
      >
        <b-row>
          <b-col class="pt-1 pl-4 pr-2 pb-0 d-flex justify-content-between">
            <div class="d-flex pb-0 pt-1">
              <h4
                qa="complementaries-cards-label"
                class="mb-1 label-dark"
              >
                {{ comp.code }}
              </h4>
              <vtBubble
                v-if="comp.attachmentsCount"
                :number="comp.attachmentsCount"
                :type="false"
                :tooltip="' '+$tc('global.file',2)"
                @click="$emit('show-complementary-attachment-tree-modal', comp.comId)"
              />
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <div class="horizontal-divider p-0 mt-0 mb-1 mx-2" />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="11"
            class="pt-0 pl-4 pr-2 pb-1"
          >
            <span qa="complementaries-cards-validation-responsible">{{ comp.responsible ? comp.responsible.fullName : '' }}</span>
            <div class="d-flex pb-0">
              <span
                v-if="comp.validationDate"
                qa="complementaries-cards-validation-date"
              >{{ comp.validationDate | datetime }}</span>
            </div>
            <div class="d-flex pb-0 mt-2">
              <b-badge
                v-if="comp.validationDate"
                class="sr-status-badge validated"
                qa="complementaries-cards-status-badge"
                pill
              >
                {{ $t('diagnosis.labels.validated') }}
              </b-badge>
              <b-badge
                v-else
                class="sr-status-badge pending"
                qa="complementaries-cards-status-badge"
                pill
              >
                {{ $t('diagnosis.labels.pending') }}
              </b-badge>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import ComplementaryCompactCard from '@/modules/diagnosisModule/views/form/complementariesView/ComplementaryCompactCard'
import complementaryServices from '@/modules/diagnosisModule/services/complementaryServices'
import { ComplementaryOperationGuardMixin } from '@/api/operations/mixins/guards/complementary-operation-guard.mixin'
import { PromiseUtils } from '@/shared/util/promise-utils'

export default {
    name: 'ComplementariesCards',
    components: {
        VtBubble,
        ComplementaryCompactCard
    },
    mixins: [
        ComplementaryOperationGuardMixin
    ],
    props: {
        labId: { type: Number, required: true },
        casId: { type: Number, required: true },
        complementaryId: {
            required: true,
            validator: prop => typeof prop === 'number' || prop === null
        },
        showCompactView: { type: Boolean, default: true }
    },
    data() {
        return {
            cardActive: null,
            complementaries: []
        }
    },
    methods: {
        async addComplementary() {
            const response = await PromiseUtils.toCallSecure(
                complementaryServices.createComplementary(this.labId, this.casId, {})
            )
            if (response) {
                this.cardActive = response
                this.showToastSuccessGlobal(this.$t('diagnosis.complementaries.complementaryCreatedSuccess'))
                this.$emit('updateCase')
                this.loadComplementaries(false, true)
            }
        },
        async loadComplementaries(select = true, compAdded = false) {
            const response = await PromiseUtils.toCallSecure(
                complementaryServices.getAllComplementariesByCaseId(this.labId, this.casId))
            if (response) {
                this.complementaries = response
                if (compAdded) {
                    const activeCard = this.complementaries.find((item) => item.comId === this.cardActive.comId)
                    this.cardSelected(activeCard ?? this.complementaries[this.complementaries.length - 1])
                } else {
                    if (this.complementaries.length && select) {
                        this.cardSelected(this.complementaries[0])
                    } else if (select) {
                        this.cardSelected(null)
                    }
                }
                this.$emit('reloadSummary')
            }
        },
        cardSelected(complementary) {
            this.$emit('cardSelected', complementary)
        }
    }
}
</script>

<style lang="scss">
.label-teaching {
  color: #00a99d;
}

.label-bank {
  color: #29abe2;
}

.label-different {
  color: #fbb03b;
}

.label-until {
  color: #d4145a;
}

.complementariesCards .badge {
  font-weight: normal;
  font-size: font-size(very-tiny) !important;
}
</style>

<style lang="scss" scoped>

.fixed-btn{
  position: sticky;
  top: 7px;
}

.blank-card {
  height: 70px;
  background-color: vt-color(base-text) !important;
  border: none;
}

.blank-card-compact {
  height: 40px;
  width: 55px;
  background-color: vt-color(base-text) !important;
  border: none;
}

.blank-message span {
  font-size: font-size(small);
  font-style: italic;
}

.validated {
  background: vt-color(green-radiant);
  color: #fff !important;
  font-weight: bold;
}

.pending {
  background: vt-color(orange-medium);
  color: #fff !important;
  font-weight: bold;
}

.complementariesCards {
  z-index: 1;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.card-active {
  border-left: 4px solid vt-color(primary);
}

.complementariesCards h4 {
  font-weight: normal;
}

.complementariesCards span {
  font-size: 0.9rem;
  color: #303133;
  margin-top: -5px;
}

.complementariesCards .arrowSub {
  position: absolute;
  right: 5px;
  bottom: 0;
}

.complementariesCards .cardBtns i {
  padding: 2px;
}

.label-teaching {
  color: #00a99d;
}

.label-bank {
  color: #29abe2;
}

.label-different {
  color: #fbb03b;
}

.label-until {
  color: #d4145a;
}

</style>
